import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, AlertTriangle } from "lucide-react";
import {
  HERO,
  TRUSTED_LOGOS,
  TRUSTED_LINE,
  PROBLEM,
  HOW_IT_WORKS,
  CASE_STUDIES,
  OFFER,
  ROLES,
  SOLUTIONS,
  INTEGRATIONS,
  FAQ,
  FINAL_CTA,
} from "../mock";
import { Reveal, SectionHeader, CheckItem, ArrowLink, DarkCTA, FaqList, CtaLink } from "../components/Shared";
import RouteMapIllustration from "../components/RouteMapIllustration";
import FleetMapIllustration from "../components/FleetMapIllustration";
import DriverAppIllustration from "../components/DriverAppIllustration";
import StackIllustration from "../components/StackIllustration";
import { Seo, organizationLd, faqLd } from "../components/Seo";
import { fetchCaseStudies, Metrics } from "./CaseStudies";

const ILLUSTRATIONS = {
  "route-map": RouteMapIllustration,
  "fleet-map": FleetMapIllustration,
  "driver-app": DriverAppIllustration,
};

/* ---------------------------------- Hero ---------------------------------- */
const Hero = () => (
  <section className="hero-fit pt-12 pb-14 md:pt-10 md:pb-14 overflow-hidden" data-testid="hero">
    <div className="container-x grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-12 items-center">
      <div>
        <span className="eyebrow-pill fade-up">{HERO.eyebrow}</span>
        <h1 className="h-display h-display-hero mt-5 fade-up delay-1">{HERO.title}</h1>
        <p className="mt-5 max-w-xl text-base md:text-lg text-[var(--fr-ink-2)] leading-relaxed fade-up delay-2">
          {HERO.subtitle}
        </p>
        <div className="mt-7 flex flex-wrap gap-3 fade-up delay-3">
          <CtaLink cta={HERO.primaryCta} className="btn btn-primary btn-lg" data-testid="hero-primary-cta" />
          <CtaLink cta={HERO.secondaryCta} className="btn btn-outline btn-lg" data-testid="hero-secondary-cta" />
        </div>
        <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-[var(--fr-muted)] fade-up delay-3" data-testid="hero-trust">
          {HERO.trust.map((t) => (
            <li key={t} className="inline-flex items-center gap-1.5">
              <Check size={14} className="text-[var(--fr-primary)]" strokeWidth={2.5} /> {t}
            </li>
          ))}
        </ul>
        <div className="mt-8 flex flex-wrap gap-8 md:gap-10 fade-up delay-4" data-testid="hero-stats">
          {HERO.stats.map((s) => (
            <div key={s.label} className="stat">
              <div className="font-display text-2xl md:text-3xl font-bold tracking-tight">{s.value}</div>
              <div className="text-xs text-[var(--fr-muted)] mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-[var(--fr-muted)] fade-up delay-4">{HERO.statsNote}</p>
      </div>

      <div className="relative fade-up delay-2 lg:max-w-[560px] lg:ml-auto w-full" data-testid="hero-visual">
        <div className="card p-3 shadow-[0_30px_60px_-30px_rgba(14,14,16,0.25)]">
          <div className="img-tile aspect-[6/5] bg-white">
            <FleetMapIllustration className="w-full h-full" />
          </div>
        </div>
        {/* floating proof chips */}
        <div className="hidden md:flex absolute -left-6 bottom-10 items-center gap-3 card px-4 py-3 shadow-lg">
          <span className="w-9 h-9 rounded-full bg-[var(--fr-primary-tint)] text-[var(--fr-primary-dark)] flex items-center justify-center">
            <Check size={16} strokeWidth={2.5} />
          </span>
          <div>
            <div className="text-sm font-semibold leading-tight">Delivered · signed</div>
            <div className="text-xs text-[var(--fr-muted)]">Marina Walk · 09:41</div>
          </div>
        </div>
        <div className="hidden md:block absolute -right-4 top-[42%] dark-block !rounded-2xl px-4 py-3 shadow-lg">
          <div className="text-[10px] uppercase tracking-[0.14em] text-white/60">SLA today</div>
          <div className="font-display text-2xl font-bold leading-tight">99.2%</div>
        </div>
      </div>
    </div>
  </section>
);

/* --------------------------------- Logos ---------------------------------- */
const ClientLogo = ({ logo }) => (
  <img
    src={logo.src}
    alt={logo.name}
    title={logo.name}
    loading="lazy"
    className={`client-logo ${logo.boost ? "client-logo-boost" : ""}`}
    style={{ height: logo.h }}
    draggable={false}
    data-testid="client-logo"
  />
);

const Logos = () => (
  <section className="py-12 border-y border-[var(--fr-border)] bg-[var(--fr-bg-alt)] overflow-hidden" data-testid="logos">
    <div className="container-x flex flex-col lg:flex-row lg:items-center gap-6">
      <div className="lg:w-1/3">
        <span className="eyebrow">Trusted by 300+ businesses</span>
        <p className="mt-2 text-[15px] text-[var(--fr-ink-2)]">{TRUSTED_LINE}</p>
      </div>
      <div className="lg:w-2/3 relative marquee-fade overflow-hidden">
        <div className="marquee-track gap-14 px-6 items-center">
          {[...TRUSTED_LOGOS, ...TRUSTED_LOGOS, ...TRUSTED_LOGOS].map((l, i) => (
            <ClientLogo key={`${l.name}-${i}`} logo={l} />
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ------------------------------- Problem/FOMO ----------------------------- */
const Problem = () => (
  <section className="section" data-testid="problem">
    <div className="container-x grid lg:grid-cols-[1fr_1.1fr] gap-12 items-center">
      <Reveal>
        <span className="eyebrow">{PROBLEM.eyebrow}</span>
        <h2 className="h-section mt-4">{PROBLEM.title}</h2>
        <p className="mt-5 text-lg text-[var(--fr-ink-2)] leading-relaxed">{PROBLEM.text}</p>

        <ul className="mt-8 divide-y divide-[var(--fr-border)] border-y border-[var(--fr-border)]" data-testid="pain-fix-list">
          {PROBLEM.pains.map((p) => (
            <li key={p.pain} className="py-3.5 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
              <span className="flex items-center gap-2 text-[14px] text-[var(--fr-muted)] line-through decoration-[var(--fr-border)]">
                <AlertTriangle size={14} className="flex-shrink-0" />
                {p.pain}
              </span>
              <ArrowRight size={14} className="text-[var(--fr-primary)]" />
              <span className="flex items-center gap-2 text-[14px] font-medium text-[var(--fr-ink)]">
                <Check size={14} className="text-[var(--fr-primary)] flex-shrink-0" strokeWidth={2.5} />
                {p.fix}
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-8 flex flex-wrap items-center gap-4">
          <CtaLink cta={HERO.primaryCta} className="btn btn-primary" data-testid="problem-cta" />
          <ArrowLink to="/features">See the full stack</ArrowLink>
        </div>
      </Reveal>

      <Reveal delay={120} className="card p-3">
        <div className="img-tile aspect-[6/5] bg-white">
          <StackIllustration className="w-full h-full" />
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------------- How it works + every tool (combined) --------------- */
const HowItWorks = () => (
  <section className="section" data-testid="how-it-works">
    <div className="container-x">
      <SectionHeader eyebrow={HOW_IT_WORKS.eyebrow} title={HOW_IT_WORKS.title} />
      <div className="mt-12 grid md:grid-cols-3 gap-5">
        {HOW_IT_WORKS.steps.map((s, i) => {
          const Illu = ILLUSTRATIONS[s.illustration];
          return (
            <Reveal key={s.n} delay={i * 80} className="card card-hover p-3 flex flex-col">
              <div className="img-tile aspect-[6/5] bg-white">{Illu && <Illu className="w-full h-full" />}</div>
              <div className="p-4 flex-1">
                <div className="text-sm font-semibold text-[var(--fr-primary-dark)]">{s.n}</div>
                <h3 className="h-card mt-3">{s.title}</h3>
                <p className="mt-3 text-[15px] text-[var(--fr-ink-2)] leading-relaxed">{s.text}</p>
              </div>
            </Reveal>
          );
        })}
      </div>

      {/* every tool, one login */}
      <div className="mt-16 pt-12 border-t border-[var(--fr-border)]" data-testid="roles">
        <Reveal className="max-w-3xl">
          <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight leading-tight">{ROLES.title}</h3>
          <p className="mt-3 text-[var(--fr-ink-2)] text-lg">{ROLES.subtitle}</p>
        </Reveal>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ROLES.items.map((r, i) => (
            <Reveal key={r} delay={(i % 4) * 60} className="card card-hover px-5 py-5 flex items-center text-[15px] font-medium dot">
              {r}
            </Reveal>
          ))}
        </div>
        <div className="mt-8">
          <ArrowLink to="/features">Explore all features</ArrowLink>
        </div>
      </div>
    </div>
  </section>
);

/* ------------------------------- Case studies ----------------------------- */
const CaseStudies = () => {
  const [cases, setCases] = useState(null);
  useEffect(() => {
    fetchCaseStudies().then((d) => setCases(d.slice(0, 3))).catch(() => setCases([]));
  }, []);
  if (cases && cases.length === 0) return null;
  return (
    <section className="section" data-testid="case-studies">
      <div className="container-x">
        <SectionHeader eyebrow={CASE_STUDIES.eyebrow} title={CASE_STUDIES.title} subtitle={CASE_STUDIES.subtitle} />
        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {(cases || []).map((c, i) => (
            <Reveal key={c.slug} delay={i * 80} className={`${i === 0 ? "dark-block !rounded-[20px]" : "card card-hover"} p-7 flex flex-col`} data-testid="case-study">
              <div className={`text-xs uppercase tracking-[0.14em] ${i === 0 ? "text-white/60" : "text-[var(--fr-muted)]"}`}>
                {c.sector} · {c.location}
              </div>
              <h3 className="font-display font-bold text-xl mt-2">{c.company}</h3>
              <p className={`mt-3 text-[15px] leading-relaxed flex-1 ${i === 0 ? "text-white/80" : "text-[var(--fr-ink-2)]"}`}>{c.title}</p>
              {c.metrics?.length > 0 && (
                <div className={`mt-6 pt-5 border-t ${i === 0 ? "border-white/15" : "border-[var(--fr-border)]"}`}>
                  <Metrics metrics={c.metrics.slice(0, 3)} dark={i === 0} compact />
                </div>
              )}
              <Link
                to={`/case-studies/${c.slug}`}
                className={`mt-6 inline-flex items-center gap-1.5 text-sm font-semibold ${i === 0 ? "text-white/80 hover:text-white" : "text-[var(--fr-primary-dark)] hover:underline"}`}
                data-testid="case-study-link"
              >
                Read the story <ArrowRight size={13} />
              </Link>
            </Reveal>
          ))}
        </div>
        <div className="mt-8">
          <Link to="/case-studies" className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--fr-primary-dark)] hover:gap-2 transition-[gap]" data-testid="all-case-studies">
            All customer stories <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </section>
  );
};

/* ---------------------------------- Offer --------------------------------- */
const Offer = () => (
  <section className="section bg-[var(--fr-bg-alt)]" data-testid="offer">
    <div className="container-x">
      <SectionHeader eyebrow={OFFER.eyebrow} title={OFFER.title} subtitle={OFFER.subtitle} />
      <Reveal className="dark-block mt-12 p-8 md:p-12 grid md:grid-cols-[1.15fr_1fr] gap-10 md:gap-14 items-center">
        <div>
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[var(--fr-primary)] text-white text-[11px] font-semibold uppercase tracking-[0.12em]">
            ★ {OFFER.badge}
          </span>
          <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-5">{OFFER.name}</h3>
          <div className="mt-4 flex items-baseline gap-3">
            <span className="font-display text-5xl md:text-6xl font-bold tracking-tight">{OFFER.price}</span>
            <span className="text-white/60 text-sm">{OFFER.priceSuffix}</span>
          </div>
          <p className="mt-4 text-white/70">{OFFER.tagline}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <CtaLink cta={HERO.primaryCta} className="btn btn-primary" data-testid="offer-trial" />
            <Link to="/pricing" className="btn btn-ghost-dark" data-testid="offer-explore">
              Compare all plans
            </Link>
          </div>
        </div>
        <div className="md:border-l md:border-white/15 md:pl-14">
          <div className="eyebrow text-white/50">What’s included</div>
          <ul className="mt-5 grid grid-cols-2 gap-x-8 gap-y-4 text-white/90 whitespace-nowrap">
            {OFFER.included.map((it) => (
              <CheckItem key={it}>{it}</CheckItem>
            ))}
          </ul>
        </div>
      </Reveal>
      <p className="mt-4 text-sm text-[var(--fr-muted)]">{OFFER.footnote}</p>

      <div className="mt-14 pt-10 border-t border-[var(--fr-border)] grid md:grid-cols-2 gap-10">
        {OFFER.addons.map((a) => (
          <div key={a.title}>
            <div className="flex items-baseline gap-3 flex-wrap">
              <h4 className="font-display font-bold text-lg">{a.title}</h4>
              <span className="text-sm text-[var(--fr-muted)]">{a.price}</span>
            </div>
            <p className="mt-2 text-[15px] text-[var(--fr-ink-2)]">{a.text}</p>
            <ArrowLink to={a.to} className="mt-3">
              Learn more
            </ArrowLink>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* -------------------------------- Solutions ------------------------------- */
const Work = () => (
  <section className="section bg-[var(--fr-bg-alt)]" data-testid="solutions-preview">
    <div className="container-x">
      <SectionHeader eyebrow="Solutions" title="Built for what you move" subtitle="Prescriptions, parcels, pallets or people — Fleetroot adapts to your operation." />
      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {SOLUTIONS.map((s, i) => (
          <Reveal key={s.slug} delay={i * 80} className="card card-hover p-3 flex flex-col">
            <div className="img-tile aspect-[4/3]">
              <img src={s.image} alt={s.name} loading="lazy" style={s.imagePosition ? { objectPosition: s.imagePosition } : undefined} />
              <span className="absolute top-3 left-3 chip bg-white/90 backdrop-blur">{s.kind}</span>
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <h3 className="h-card">{s.name}</h3>
              <p className="mt-2 text-[15px] text-[var(--fr-ink-2)]">{s.tagline}</p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {s.tags.slice(0, 3).map((t) => (
                  <span key={t} className="chip">
                    {t}
                  </span>
                ))}
              </div>
              <ArrowLink to={`/solutions#${s.slug}`} className="mt-5">
                Learn more
              </ArrowLink>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------------------ Integrations ------------------------------ */
const Integrations = () => (
  <section className="py-12 border-y border-[var(--fr-border)] bg-[var(--fr-bg-alt)]" data-testid="integrations-strip">
    <div className="container-x flex flex-col lg:flex-row lg:items-center gap-6">
      <div className="lg:w-1/3">
        <span className="eyebrow">Works with your stack</span>
        <p className="mt-2 text-[15px] text-[var(--fr-ink-2)]">Orders in, tracking and proof back — through native connectors, REST API and webhooks.</p>
      </div>
      <div className="flex flex-wrap gap-2 lg:w-2/3">
        {INTEGRATIONS.map((n) => (
          <span key={n} className="chip !bg-white !text-[14px] !px-3.5 !py-1.5 font-medium">
            {n}
          </span>
        ))}
        <Link to="/features#integrations" className="chip !bg-[var(--fr-primary-tint)] !border-transparent !text-[14px] !px-3.5 !py-1.5 font-semibold text-[var(--fr-primary-dark)]">
          + API & webhooks
        </Link>
      </div>
    </div>
  </section>
);

/* ----------------------------------- FAQ ---------------------------------- */
const Faq = () => (
  <section className="section" data-testid="faq-section">
    <div className="container-x grid lg:grid-cols-[1fr_2fr] gap-12">
      <SectionHeader title="Frequently asked questions" subtitle="Everything you need to know before you start." />
      <Reveal>
        <FaqList items={FAQ} />
      </Reveal>
    </div>
  </section>
);

const Home = () => (
  <>
    <Seo title="Fleetroot — Fleet management & last-mile delivery, simplified." jsonLd={[organizationLd(), faqLd(FAQ)]} />
    <Hero />
    <Logos />
    <Problem />
    <Work />
    <CaseStudies />
    <Offer />
    <HowItWorks />
    <Integrations />
    <Faq />
    <DarkCTA title={FINAL_CTA.title} text={FINAL_CTA.text} cta={FINAL_CTA.cta} secondary={FINAL_CTA.secondary} email={FINAL_CTA.email} />
  </>
);

export default Home;
